/**
 * @sazeo/sdk — Goals & OKRs hooks
 *
 * TanStack Query wrappers for the goals addon. All calls route through
 * the Edge Function at `/addons/goals/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Goal {
  id: string;
  tenant_id: string;
  title: string;
  description: string | null;
  owner_id: string | null;
  kind: 'individual' | 'team' | 'company';
  status: 'draft' | 'active' | 'achieved' | 'missed' | 'cancelled';
  period_start: string | null;
  period_end: string | null;
  progress: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface KeyResult {
  id: string;
  tenant_id: string;
  goal_id: string;
  title: string;
  target: number;
  current: number;
  unit: string;
  status: 'on_track' | 'at_risk' | 'off_track' | 'completed';
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface GoalCreateInput {
  title: string;
  description?: string | null;
  owner_id?: string | null;
  kind?: Goal['kind'];
  period_start?: string | null;
  period_end?: string | null;
}

export interface GoalUpdateInput extends Partial<GoalCreateInput> {
  status?: Goal['status'];
  progress?: number;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const goalKeys = {
  all: ['goals'] as const,
  list: (tenantId: string) => [...goalKeys.all, 'list', tenantId] as const,
  mine: (tenantId: string) => [...goalKeys.all, 'mine', tenantId] as const,
  keyResults: (goalId: string) => [...goalKeys.all, 'key-results', goalId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useGoals(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: goalKeys.list(tenantId),
    queryFn: () => apiFetch<Goal[]>(supabase, 'addons/goals/goals'),
    enabled: Boolean(tenantId),
  });
}

export function useMyGoals(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: goalKeys.mine(tenantId),
    queryFn: () => apiFetch<Goal[]>(supabase, 'addons/goals/goals/mine'),
    enabled: Boolean(tenantId),
  });
}

export function useKeyResults(supabase: SupabaseClientLike, tenantId: string, goalId: string) {
  return useQuery({
    queryKey: goalKeys.keyResults(goalId),
    queryFn: () => apiFetch<KeyResult[]>(supabase, `addons/goals/goals/${goalId}/key-results`),
    enabled: Boolean(tenantId) && Boolean(goalId),
  });
}

export function useCreateGoal(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GoalCreateInput) =>
      apiFetch<Goal>(supabase, 'addons/goals/goals', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: goalKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: goalKeys.mine(tenantId) });
    },
  });
}

export function useUpdateGoal(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: GoalUpdateInput }) =>
      apiFetch<Goal>(supabase, `addons/goals/goals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: goalKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: goalKeys.mine(tenantId) });
    },
  });
}

export function useDeleteGoal(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/goals/goals/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: goalKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: goalKeys.mine(tenantId) });
    },
  });
}

export interface KeyResultCreateInput {
  goal_id: string;
  title: string;
  target_value?: number;
}

export function useCreateKeyResult(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: KeyResultCreateInput) =>
      apiFetch<KeyResult>(supabase, 'addons/goals/key-results', {
        method: 'POST',
        body: JSON.stringify({ tenant_id: tenantId, ...input }),
      }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: goalKeys.keyResults(vars.goal_id) });
    },
  });
}
